<template>
  <div
    :class="[
      'top-0 left-0 w-full h-full flex flex-col',
      { 'sidebar-collapsed': splitterStore.isCollapsed && !viewportStore.isMobile },
    ]"
  >
    <div
      v-if="!viewportStore.isMobile"
      class="flex items-center justify-between pl-6 pr-2 pt-4 pb-2"
    >
      <div :class="logoContainerClasses">
        <img alt="logo" src="@/assets/logo.png" :width="logoWidth" />
      </div>
    </div>

    <div
      class="sidebar-content scrollbar-stealth-hover flex-1 overflow-y-auto"
      :class="splitterStore.isCollapsed && !viewportStore.isMobile ? 'pt-2' : ''"
    >
      <slot></slot>
    </div>

    <div class="px-2 pb-10">
      <hr
        :class="['mx-2 mt-0 border-none block bg-white', viewportStore.isMobile ? 'mb-4' : 'mb-10']"
        style="height: 1px"
      />

      <div v-if="footerNavigationItem?.route" class="p-panelmenu-item depth-0" role="button">
        <div class="p-panelmenu-content" role="button">
          <router-link
            v-ripple
            :to="footerNavigationItem.route"
            :class="[
              isActive(footerNavigationItem.route) ? 'active-link' : 'no-active-link',
              'p-panelmenu-item-link',
            ]"
            @click="closeMobileSidebar"
          >
            <span class="pi pi-cog p-panelmenu-item-icon" />
            <span class="p-panelmenu-item-label">{{ t(`${footerNavigationItem?.title}`) }}</span>
          </router-link>
        </div>
      </div>

      <!-- Mobile Footer Menu Items -->
      <div v-if="viewportStore.isMobile" class="mobile-footer-menu flex flex-col gap-4">
        <MobileLanguageSelector />

        <!-- Logout Item -->
        <div class="p-panelmenu-item depth-0" role="button">
          <div class="p-panelmenu-content" role="button">
            <a v-ripple class="p-panelmenu-item-link" @click="handleLogout">
              <span class="pi pi-sign-out p-panelmenu-item-icon" />
              <span class="p-panelmenu-item-label">{{ t('navigation.logout') }}</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { useLogout } from '@/composables/useLogout';
import { useNavigation } from '@/composables/useRoute';
import { useSplitterStore } from '@/stores/navsplitter';
import { useViewportStore } from '@/stores/viewport';
import { findNavigationItem } from '@/router/navigation';

import MobileLanguageSelector from './MobileLanguageSelector.vue';

const footerNavigationItem = computed(() => findNavigationItem('administration'));
const { t } = useI18n();
const { isActive } = useNavigation();
const { logout } = useLogout();

const splitterStore = useSplitterStore();
const viewportStore = useViewportStore();

const handleLogout = () => {
  logout();
};

const closeMobileSidebar = () => {
  if (viewportStore.isMobile) {
    viewportStore.closeMobileSidebar();
  }
};

// Berechne, ob das Panel schmal ist
const isPanelNarrow = computed(() => splitterStore.panelSizeLeftInPixels < 150);

const logoContainerClasses = computed(() => {
  return [
    'logo-container',
    'w-full',
    'pl-2',
    'pt-2',
    isPanelNarrow.value ? 'align-right' : 'align-left',
    isPanelNarrow.value ? 'pl-2' : 'px-4',
    splitterStore.panelSizeLeftInPixels < 100 ? 'signet' : '',
  ];
});

// Dynamic logo size
const logoWidth = computed(() => (isPanelNarrow.value ? 80 : 130));
</script>

<style scoped lang="css">
.sidebar-hamburger {
  transition: all 0.3s ease;
}

.sidebar-hamburger.collapsed {
  margin: 0 auto;
}

.sidebar-hamburger.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.sidebar-collapsed {
  transition: all 0.3s ease;
}

/* Hide all content except hamburger when collapsed */
.sidebar-collapsed .sidebar-content,
.sidebar-collapsed .px-2.pb-10,
.sidebar-collapsed .logo-container {
  display: none;
}

.sidebar-collapsed .flex.items-center.pt-4.pb-2 {
  justify-content: center;
  padding: 0.5rem !important;
}

.sidebar-collapsed :deep(.p-panelmenu-header-link),
.sidebar-collapsed :deep(.p-panelmenu-item-link) {
  justify-content: center;
  padding: 0.75rem;
}

.sidebar-collapsed :deep(.p-panelmenu-header-icon),
.sidebar-collapsed :deep(.p-panelmenu-item-icon),
.sidebar-collapsed :deep(.p-panelmenu-footer-icon) {
  margin: 0;
  font-size: 1.25rem;
}

.sidebar {
  position: absolute;
  transition: transform 0.3s ease-in-out;
  transform: translateX(-100%);
}

.sidebar.is-open {
  transform: translateX(0);
}

:root {
  --sidebar-scrollbar-thumb-rgb: 55, 65, 81; /* #3B82F6 als Beispiel */
  --sidebar-scrollbar-track-rgb: 31, 41, 55;
}

/* Invisible when idle; fades in on hover/focus; stable gutter */
:deep(.scrollbar-stealth-hover) {
  overflow-y: auto;
  scrollbar-gutter: stable both-edges;

  /* Firefox: fully transparent when idle (no animation possible) */
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
}

/* Firefox: show thumb on hover/focus (snap-in due to browser limits) */
:deep(.scrollbar-stealth-hover:hover),
:deep(.scrollbar-stealth-hover:focus-within) {
  scrollbar-color: rgba(var(--sidebar-scrollbar-thumb-rgb), 0.8) transparent;
}

/* WebKit (Chrome/Edge/Safari): keep fixed width (no jump) */
:deep(.scrollbar-stealth-hover::-webkit-scrollbar) {
  width: 10px;
}

/* WebKit: thumb is fully transparent & non-visible by default */
:deep(.scrollbar-stealth-hover::-webkit-scrollbar-thumb) {
  background-color: rgba(var(--sidebar-scrollbar-thumb-rgb), 0.8); /* final color */
  border-radius: 8px;
  border: 3px solid transparent; /* keeps geometry stable */
  background-clip: padding-box;

  /* Smooth fade in/out on WebKit */
  opacity: 0; /* invisible at rest */
  transition:
    opacity 0.25s ease,
    background-color 0.25s ease;
}

/* WebKit: track transparent so sidebar shows through */
:deep(.scrollbar-stealth-hover::-webkit-scrollbar-track) {
  background: transparent;
}

/* WebKit: fade in on hover/focus */
:deep(.scrollbar-stealth-hover:hover::-webkit-scrollbar-thumb),
:deep(.scrollbar-stealth-hover:focus-within::-webkit-scrollbar-thumb) {
  opacity: 1; /* smooth fade in */
}

/* Mobile footer menu */
.mobile-footer-menu {
  margin-bottom: 1rem;
}
</style>
